/**
 * What the early-access form accepts, before anything is sent.
 *
 * The same shape rules the server applies (`public-site/early-access.ts`),
 * so a submission the page lets through is one the server will store. Pure
 * functions over the field values; the hook decides when each is shown.
 */

export type FieldName = 'name' | 'email' | 'brand' | 'link';

/** Form order — the order errors are listed in, and the order fields are tabbed. */
export const FIELD_ORDER: readonly FieldName[] = ['name', 'email', 'brand', 'link'];

export interface FormValues {
  readonly name: string;
  readonly email: string;
  readonly brand: string;
  readonly link: string;
}

export const EMPTY_VALUES: FormValues = { name: '', email: '', brand: '', link: '' };

/** One `@`, something either side, a dot in the domain, no whitespace. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** `@brand`, `brand_name`, `brand.sa` as an account. */
const HANDLE_SHAPE = /^@?[\w.]{2,}$/;
/** `brand.sa`, `https://brand.sa/shop`. */
const DOMAIN_SHAPE = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i;

export function emailOk(value: string): boolean {
  return EMAIL_SHAPE.test(value.trim());
}

/** Empty is fine — the field is optional. One link, no whitespace, URL or account. */
export function linkOk(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  if (/\s/.test(trimmed)) return false;
  return HANDLE_SHAPE.test(trimmed) || DOMAIN_SHAPE.test(trimmed);
}

function fieldOk(field: FieldName, values: FormValues): boolean {
  switch (field) {
    case 'name':
      return values.name.trim().length > 0;
    case 'email':
      return emailOk(values.email);
    case 'brand':
      return values.brand.trim().length > 0;
    case 'link':
      return linkOk(values.link);
  }
}

/** The fields that would be refused, in form order. Empty means ready. */
export function invalidFields(values: FormValues): readonly FieldName[] {
  return FIELD_ORDER.filter((field) => !fieldOk(field, values));
}
